import type { UpdatedData } from '@apps/registry/admin/routes/YourRegistry/components/PageSettingsDialog/PageSettingsDialog.provider';
import { createTelemetry, createTelemetryObject } from '@shared/core/analytics';
import type { PageFunctionType } from '@shared/core/analytics/telemetry/types';
import type { ReEngagementPopupItemTypes } from './components/DashboardContentV2/ReEngagementPopup/constants/ReEngagementPopup.const';

const category = 'AdminDashboard';
const pagePrefix = 'admin';

export type PagesForTelemetry =
  | 'dashboard'
  | 'productSelector'
  | 'visibilityDialog'
  | 'multiProductPromoDialog'
  | 'reEngagementPopup'
  | 'thingsToDoAnnouncement'
  | 'honeymoonAnnouncement'
  | 'honeymoonPaintedDoor';

const pages: { [tab in PagesForTelemetry]: PageFunctionType } = {
  dashboard: ({ dashboardVersion }: { dashboardVersion: string }) => ({
    category,
    pagePrefix,
    page: 'Dashboard',
    action: 'AdminVisited',
    extraPageInfo: {
      dashboardVersion,
    },
  }),
  productSelector: () => ({
    category,
    pagePrefix,
    page: 'EventProductSettingsDialog',
    action: 'ViewProductSelector',
  }),
  visibilityDialog: (pageName: string) => ({
    category: 'dashboard',
    pagePrefix: 'admin',
    page: pageName,
    action: 'VisibilityDialog',
  }),
  multiProductPromoDialog: () => ({
    category: 'dashboard',
    pagePrefix: 'admin',
    page: 'multiProductPromoDialog',
    action: 'PageViewed',
    extraInfo: {
      name: 'admin.dashboard.multiProductPromoDialog',
    },
  }),
  reEngagementPopup: () => ({
    category: 'dashboard',
    pagePrefix: 'admin',
    page: 'engagement_popup',
    action: 'PageViewed',
    extraInfo: {
      name: 'admin.dashboard.engagement_popup',
    },
  }),
  thingsToDoAnnouncement: () => ({
    category,
    pagePrefix,
    page: 'things_to_do_announcement',
    action: 'PageViewed',
    extraInfo: {
      name: 'admin.dashboard.things_to_do_announcement',
    },
  }),
  honeymoonAnnouncement: () => ({
    category,
    pagePrefix,
    page: 'honeymoon_announcement',
    action: 'PageViewed',
    extraInfo: {
      name: 'admin.dashboard.honeymoon_announcement',
    },
  }),
  honeymoonPaintedDoor: () => ({
    category,
    pagePrefix,
    page: 'honeymoon_painted_door',
    action: 'PageViewed',
    extraInfo: {
      name: 'admin.dashboard.honeymoon_painted_door',
    },
  }),
};

export const dashboardTelemetry = createTelemetryObject({
  actions: {
    openPage: ({ label, section, categoryArg, pageSlug }: { label: string; section: string; categoryArg?: string; pageSlug?: string }) => ({
      action: 'OpenPage',
      category: categoryArg ? categoryArg : category,
      actionType: 'click',
      label,
      extraInfo: {
        section,
        pageSlug,
      },
    }),
    visibilityUpdated: ({ visibility, visibilityChanged, eventPasswordChanged, label }: UpdatedData & { label: string }) => ({
      action: 'Updated',
      page: 'visibilityDialog',
      category,
      label,
      extraInfo: {
        visibility,
        visibilityChanged,
        eventPasswordChanged,
      },
    }),
    designTabClicked: () => ({
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Design Tab',
    }),
    shareClicked: () => ({
      action: 'Share',
      category,
      actionType: 'click',
      extraInfo: {
        section: 'EventSettings',
      },
      label: 'event',
    }),
    productSelectionSave: (selectedOptions: string[], removedOptions: string[], addedOptions: string[]) => ({
      action: 'EventProductSettingsUpdated',
      category,
      extraInfo: {
        selectedOptions,
        removedOptions,
        addedOptions,
      },
      label: 'saved',
    }),
    retargetDialogCTAClicked: () => ({
      category: 'AdminDashboard',
      action: 'ButtonInteracted',
      label: 'Learn More',
      extraInfo: {
        name: 'adminRoomBlockCTA_retargetpopup',
      },
    }),
    retargetDialogDismissClicked: () => ({
      category: 'AdminDashboard',
      action: 'ButtonInteracted',
      label: 'adminRoomBlockDismiss',
      extraInfo: {
        name: 'adminRoomBlockDismiss_retargetpopup',
      },
    }),
    addShippingAddressButtonBannerClicked: () => ({
      category: 'AdminDashboard',
      action: 'ButtonInteracted',
      label: 'addShippingAddressButtonBanner',
    }),

    // Re-engagement Popup
    reEngagementPopupCTAClicked: (popupType: ReEngagementPopupItemTypes, action: 'clicked' | 'dismissed') => ({
      category: 'hotelBlockConfirmationAbandonment',
      action: 'engagementPopupInteracted',
      label: 'engagement_popup',
      extraInfo: {
        name: action,
        popupType: popupType,
      },
    }),
    printReEngagementPopupCTAClicked: (name: 'viewDrafts' | 'editDraft', action: 'clicked' | 'dismissed') => ({
      category: 'AdminDashboard',
      action: 'engagementPopupInteracted',
      label: 'engagement_popup',
      extraInfo: {
        name: `${name}_${action}`,
        popupType: 'print',
      },
    }),
    hotelReminderReEngagementPopupViewed: (args: {
      eventDate: string;
      title: string;
      subtitle: string;
      variant: ReEngagementPopupItemTypes.HOTEL_REMINDER_1_MONTH | ReEngagementPopupItemTypes.HOTEL_REMINDER_3_MONTH | ReEngagementPopupItemTypes.HOTEL_REMINDER_6_MONTH;
    }) => ({
      category: 'AdminDashboard',
      action: 'engagementPopupViewed',
      label: 'engagement_popup',
      extraInfo: {
        popupType: 'Guest Hotel Reminder',
        eventDate: args.eventDate,
        title: args.title,
        subtitle: args.subtitle,
        variant: args.variant,
      },
    }),
    hotelReminderReEngagementPopupCTAClicked: (args: {
      eventDate: string;
      title: string;
      subtitle: string;
      variant: ReEngagementPopupItemTypes.HOTEL_REMINDER_1_MONTH | ReEngagementPopupItemTypes.HOTEL_REMINDER_3_MONTH | ReEngagementPopupItemTypes.HOTEL_REMINDER_6_MONTH;
      action: 'clicked' | 'dismissed';
    }) => ({
      category: 'AdminDashboard',
      action: 'engagementPopupInteracted',
      label: 'engagement_popup',
      extraInfo: {
        popupType: 'Guest Hotel Reminder',
        eventDate: args.eventDate,
        title: args.title,
        subtitle: args.subtitle,
        variant: args.variant,
        action: args.action,
      },
    }),
    addNewPageOpen: () => ({
      action: 'WebsiteAdminButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Open Add New Page Dialog',
      extraInfo: {
        name: 'openAddNewPageDialog',
      },
    }),
    thingsToDoAnnouncementClicked: () => ({
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Explore Things to Do',
    }),
    thingsTodoAnnouncementDismissed: () => ({
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'I’ll do this later',
    }),
    honeymoonAnnouncementEnter: () => ({
      action: 'PageViewed',
      category: 'website',
      pagePrefix: 'admin',
      label: 'honeymoon_announcement',
    }),
    honeymoonAnnouncementClicked: () => ({
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Get a free intro to a local expert',
    }),
    honeymoonAnnouncementDismissed: () => ({
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Remind me later - Honeymoon survey',
    }),
    honeymoonAnnouncementNotInterested: () => ({
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: "I'm not interested - Honeymoon survey",
    }),
    honeymoonFormSubmitted: ({ eventId, eventHandle, formData }: { eventId: string; eventHandle: string; formData: Record<string, string> }) => ({
      action: 'FormSubmitted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'honeymoon_form',
      extraInfo: {
        eventId,
        eventHandle,
        formData,
      },
    }),
    // Success state shown after a successful survey submit. Custom 'FormSubmissionSuccess' never reached
    // Amplitude — reuse approved PageViewed (impression) with a distinct label.
    honeymoonFormSubmissionSuccess: () => ({
      action: 'PageViewed',
      category: 'website',
      pagePrefix: 'admin',
      label: 'honeymoon_form_submission',
    }),
    honeymoonConfirmationClicked: () => ({
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Sounds Good - Honeymoon survey',
    }),
    honeymoonPaintedDoorEntered: ({ version, environment }: { version: string; environment: string }) => ({
      action: 'PageViewed',
      category: 'website',
      pagePrefix: 'admin',
      label: 'honeymoon_painted_door',
      extraInfo: { version, environment },
    }),
    honeymoonPaintedDoorTryIdeaClicked: ({ version, environment }: { version: string; environment: string }) => ({
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Try your idea - Honeymoon painted door',
      extraInfo: { version, environment },
    }),
    honeymoonPaintedDoorBrowseDestinationsClicked: ({ version, environment }: { version: string; environment: string }) => ({
      // The "More…" chip beside the destinations. It leaves the dashboard for the honeymoon page, so
      // without this the funnel would read that exit as a couple who saw the door and did nothing.
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Browse destinations - Honeymoon painted door',
      extraInfo: { version, environment },
    }),
    honeymoonPaintedDoorDemoClicked: ({ clickCount, version, environment }: { clickCount: number; version: string; environment: string }) => ({
      // Couples sometimes click the non-interactive demo animation expecting it to be tappable.
      // ButtonInteracted is an approved action that reaches Amplitude; clickCount = cumulative
      // confused clicks this session (debounced bursts), for confusion intensity.
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Demo animation clicked - Honeymoon painted door',
      extraInfo: { clickCount, version, environment },
    }),
    honeymoonPaintedDoorDemoCompleted: ({ version, environment }: { version: string; environment: string }) => ({
      // Passive impression (demo animation finished) — PageViewed is an approved action that reaches
      // Amplitude. Custom action names (e.g. 'DemoCompleted') are dropped before ingestion. Distinct
      // label keeps it separable from the door-entered PageViewed.
      action: 'PageViewed',
      category: 'website',
      pagePrefix: 'admin',
      label: 'honeymoon_painted_door_demo_completed',
      extraInfo: { version, environment },
    }),
    honeymoonPaintedDoorPromptSubmitted: ({
      captureDestination,
      destination,
      promptId,
      promptLength,
      version,
      environment,
    }: {
      // Where the destination actually went. Was hardcoded to `google_form`, which stopped being
      // true once a version handed off to the search instead — every arm would have claimed a
      // capture that never happened, and a filter on it would have mis-split the experiment.
      captureDestination: 'google_form' | 'honeymoon_search';
      /**
       * The place the couple chose, on the arms that hand off to a real search.
       *
       * Deliberately absent on a `google_form` arm: there the prompt is free text the couple typed,
       * which belongs in the capture form and not in an analytics property. A handoff arm has no
       * form, so without this the funnel could see THAT a couple clicked through but never WHERE —
       * and "did they pick a destination we have no inventory for" is the first question a weak
       * downstream conversion raises.
       */
      destination?: string;
      promptId: string;
      promptLength: number;
      version: string;
      environment: string;
    }) => ({
      action: 'FormSubmitted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'honeymoon_painted_door_prompt',
      extraInfo: {
        captureDestination,
        ...(destination && { destination }),
        promptId,
        promptLength,
        version,
        environment,
      },
    }),
    honeymoonPaintedDoorWaitlistResponseShown: ({ version, environment }: { version: string; environment: string }) => ({
      // Passive impression (fake waitlist response shown) — reuse approved PageViewed action with a
      // distinct label. Custom 'WaitlistResponseShown' never reaches Amplitude.
      action: 'PageViewed',
      category: 'website',
      pagePrefix: 'admin',
      label: 'honeymoon_painted_door_waitlist_response',
      extraInfo: { version, environment },
    }),
    honeymoonPaintedDoorBackToDashboardClicked: ({ version, environment }: { version: string; environment: string }) => ({
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Back to Dashboard - Honeymoon painted door',
      extraInfo: { version, environment },
    }),
    honeymoonPaintedDoorMinimized: ({
      phase,
      source,
      waitlistAdded,
      version,
      environment,
    }: {
      phase: string;
      source: string;
      waitlistAdded: boolean;
      version: string;
      environment: string;
    }) => ({
      // Triggered by the close/back-to-dashboard click — reuse approved ButtonInteracted action.
      // Custom 'ModalMinimized' never reaches Amplitude. phase/source/waitlistAdded carry the detail.
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Minimize - Honeymoon painted door',
      extraInfo: {
        phase,
        source,
        waitlistAdded,
        version,
        environment,
      },
    }),
    honeymoonPaintedDoorMiniCardReopened: ({ version, environment }: { version: string; environment: string }) => ({
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Reopen mini card - Honeymoon painted door',
      extraInfo: { version, environment },
    }),
    honeymoonPaintedDoorMiniCardDismissed: ({ waitlistAdded, version, environment }: { waitlistAdded: boolean; version: string; environment: string }) => ({
      action: 'ButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Dismiss mini card - Honeymoon painted door',
      extraInfo: {
        waitlistAdded,
        version,
        environment,
      },
    }),
  },
  pages,
});

const {
  TelemetryProvider: DashboardTelemetryProvider,
  useTelemetry: useDashboardTelemetry,
  enrichTelemetryExtraInfo: enrichDashboardTelemetryExtraInfo,
} = createTelemetry(dashboardTelemetry, { eventId: '' });

type DashboardTelemetry = ReturnType<typeof useDashboardTelemetry>;

export { DashboardTelemetryProvider, useDashboardTelemetry, enrichDashboardTelemetryExtraInfo };
export type { DashboardTelemetry };
